import type {
  CardioState, CoreState, ProfileState, PushUpsState, ScoreResult, WhtrState,
} from './types'
import {
  PU_STD_M, PU_STD_F, PU_HR_M, PU_HR_F,
  SITUP_M, SITUP_F, CLRC_M, CLRC_F, PLANK_M, PLANK_F,
  RUN_M, RUN_F, HAMR_M, HAMR_F,
} from './scoringTables'
import {
  getAG, lookupHigh, lookupLow, lookupWHtR, getWhtrRiskLabel,
  toMMSS, parseRunTime, parsePlankTime, compositeToCategory,
} from './scoringHelpers'
import { isFieldEntered, parseAgeField, parseIntField } from './scoringInput'

export interface ScoringInputs {
  profile: ProfileState
  pushUps: PushUpsState
  core: CoreState
  cardio: CardioState
  whtr: WhtrState
}

export function isComponentFilled(
  pushUps: PushUpsState,
  core: CoreState,
  cardio: CardioState,
  whtr: WhtrState,
): boolean {
  if (!pushUps.exempt && !isFieldEntered(pushUps.reps)) return false
  if (!core.exempt) {
    if (core.type === 'plank') {
      if (!isFieldEntered(core.plankMin) && !isFieldEntered(core.plankSec)) return false
    } else if (!isFieldEntered(core.reps)) return false
  }
  if (!cardio.exempt) {
    if (cardio.type === 'run') {
      if (!isFieldEntered(cardio.runMin) && !isFieldEntered(cardio.runSec)) return false
    } else if (!isFieldEntered(cardio.shuttles)) return false
  }
  if (!whtr.exempt) {
    if (!isFieldEntered(whtr.waist) || !isFieldEntered(whtr.height)) return false
  }
  return true
}

export function isScoreInputsComplete(inputs: ScoringInputs): boolean {
  if (parseAgeField(inputs.profile.age) === null) return false
  return isComponentFilled(inputs.pushUps, inputs.core, inputs.cardio, inputs.whtr)
}

/** Returns null when age is invalid; otherwise computes full PFRA result from current inputs. */
export function buildScoreResult(inputs: ScoringInputs): ScoreResult | null {
  const { profile, pushUps, core, cardio, whtr } = inputs
  const ageNum = parseAgeField(profile.age)
  if (ageNum === null) return null

  const ag = getAG(ageNum)

  const puEx = pushUps.exempt
  let puScore = 0, puDetail = '', puMin = 7.5
  if (!puEx) {
    const reps = parseIntField(pushUps.reps)
    const table = pushUps.type === 'standard'
      ? (profile.sex === 'male' ? PU_STD_M : PU_STD_F)
      : (profile.sex === 'male' ? PU_HR_M : PU_HR_F)
    puScore = lookupHigh(table, ag, reps)
    puDetail = `${reps} reps`
    const minEntry = table[ag]?.[table[ag].length - 1]
    puMin = minEntry ? minEntry[1] : 7.5
  }

  const coreEx = core.exempt
  let coreScore = 0, coreDetail = ''
  if (!coreEx) {
    if (core.type === 'plank') {
      const secs = parsePlankTime(core.plankMin, core.plankSec)
      coreScore = lookupHigh(profile.sex === 'male' ? PLANK_M : PLANK_F, ag, secs)
      coreDetail = toMMSS(secs)
    } else {
      const reps = parseIntField(core.reps)
      const table = core.type === 'situp'
        ? (profile.sex === 'male' ? SITUP_M : SITUP_F)
        : (profile.sex === 'male' ? CLRC_M : CLRC_F)
      coreScore = lookupHigh(table, ag, reps)
      coreDetail = `${reps} reps`
    }
  }

  const cardioEx = cardio.exempt
  let cardioScore = 0, cardioDetail = ''
  if (!cardioEx) {
    if (cardio.type === 'run') {
      const secs = parseRunTime(cardio.runMin, cardio.runSec)
      cardioScore = secs > 0 ? lookupLow(profile.sex === 'male' ? RUN_M : RUN_F, ag, secs) : 0
      cardioDetail = secs > 0 ? toMMSS(secs) : '-'
    } else {
      const shuttles = parseIntField(cardio.shuttles)
      cardioScore = shuttles > 0 ? lookupHigh(profile.sex === 'male' ? HAMR_M : HAMR_F, ag, shuttles) : 0
      cardioDetail = `${shuttles} shuttles`
    }
  }

  const whtrEx = whtr.exempt
  let whtrScore = 0, whtrDetail = ''
  if (!whtrEx) {
    const w = parseFloat(whtr.waist)
    const h = parseFloat(whtr.height)
    if (w > 0 && h > 0) {
      const rawRatio = w / h
      const ratio = Math.floor(rawRatio * 100) / 100
      whtrScore = lookupWHtR(ratio)
      const risk = getWhtrRiskLabel(ratio)
      whtrDetail = `WHtR ${rawRatio.toFixed(3)} → ${ratio.toFixed(2)} - ${risk.label}`
    }
  }

  const compMap = [
    { score: puScore, max: 15, exempt: puEx },
    { score: coreScore, max: 15, exempt: coreEx },
    { score: cardioScore, max: 50, exempt: cardioEx },
    { score: whtrScore, max: 20, exempt: whtrEx },
  ]
  const activeComps = compMap.filter(c => !c.exempt)
  const totalMaxActive = activeComps.reduce((s, c) => s + c.max, 0)
  const rawSum = activeComps.reduce((s, c) => s + c.score, 0)
  const composite = totalMaxActive > 0 ? (rawSum / totalMaxActive * 100) : 0

  const cat = compositeToCategory(composite)

  const fails: string[] = []
  if (!puEx && isFieldEntered(pushUps.reps) && puScore < puMin) fails.push('Push-ups below minimum')
  if (!cardioEx) {
    const hasCardioInput = cardio.type === 'run'
      ? !!(cardio.runMin || cardio.runSec)
      : !!cardio.shuttles
    if (hasCardioInput && cardioScore < 29.5) fails.push('Cardio below minimum')
  }

  return {
    sex: profile.sex,
    age: ageNum,
    ag,
    testDate: '',
    puType: pushUps.type,
    puScore,
    puDetail,
    puEx,
    coreType: core.type,
    coreScore,
    coreDetail,
    coreEx,
    cardioType: cardio.type,
    cardioScore,
    cardioDetail,
    cardioEx,
    whtrScore,
    whtrDetail,
    whtrEx,
    composite,
    cat,
    fails,
  }
}